import React from "react";

import clsx from "clsx";

import { Label, Link, UnstyledLink } from "@busbud/horizon";

import { ImgixLazyImage } from "@app/components/imgix-lazy-image";
import { useAppContext } from "@app/helpers/hooks";
import URIExpand from "@app/lib/uri-expand";

type Props = {
  operator_name: string;
  display_operated_by?: boolean;
  logo_url: string | null;
  className?: string;
  href?: string | null;
  no_wrap?: boolean;
  size: Size;
};

type Size = "sm" | "md" | "lg" | "xl";

const container_height_classname: Record<Size, string> = {
  sm: "max-h-150",
  md: "max-h-300",
  lg: "max-h-400",
  xl: "max-h-600"
};

const image_heights: Record<Size, string> = {
  sm: "12",
  md: "24",
  lg: "32",
  xl: "48"
};

const OperatorLogo: React.FC<Props> = ({
  operator_name,
  display_operated_by = false,
  logo_url,
  className,
  href,
  size,
  no_wrap
}) => {
  const container_classnames = clsx(
    container_height_classname[size],
    className
  );

  if (!logo_url) {
    return (
      <div className={container_classnames}>
        {display_operated_by ? (
          <OperatedByLabel
            operator_name={operator_name}
            no_wrap={no_wrap}
            url={href}
          />
        ) : (
          <OperatorLabel operator_name={operator_name} href={href} />
        )}
      </div>
    );
  }

  const image_url = URIExpand(logo_url, {
    height: image_heights[size],
    width: "auto"
  });
  const image_url_query = image_url.query(true);

  const final_image_url = image_url
    .setQuery({
      trim: "auto",
      "trim-md": image_url_query["trim-md"] || 0,
      bg: undefined
    })
    .toString();

  const image = (
    <ImgixLazyImage
      alt={operator_name}
      className={container_classnames}
      template_url={final_image_url}
      densities={[1, 2, 3]}
    />
  );

  if (href) {
    return (
      <UnstyledLink className="flex w-full items-center" url={href}>
        {image}
      </UnstyledLink>
    );
  }

  return image;
};

const OperatedByLabel: React.FC<{
  operator_name: string;
  no_wrap?: boolean;
  url?: string | null;
}> = ({ operator_name, no_wrap, url }) => {
  const { translator } = useAppContext();

  return (
    <div className="flex flex-col">
      <Label
        as="div"
        fontWeight="bold"
        size="xxs"
        className="text-color-tertiary"
      >
        {translator.t("!departure.details.operated-by")}
      </Label>
      <Label
        as="div"
        className="text-color-secondary [overflowWrap:anywhere]"
        fontWeight="bold"
        noWrap={no_wrap}
        size="md"
        title={no_wrap ? operator_name : undefined}
      >
        {url ? <Link url={url}>{operator_name}</Link> : operator_name}
      </Label>
    </div>
  );
};

const OperatorLabel: React.FC<{
  operator_name: string;
  href?: string | null;
}> = ({ operator_name, href }) => {
  return (
    <Label as="span">
      {href ? <Link>{operator_name}</Link> : operator_name}
    </Label>
  );
};

export { OperatorLogo };
